import type { ReconciliationSession } from '../types'

export const STATUS_LABEL: Record<string, string> = {
  OK: 'OK',
  PARTIAL_DELIVERY: 'Deellevering',
  DELIVERED_LT_CONFIRMED: 'Nog niet compleet geleverd',
  INVOICED_LT_DELIVERED: 'Deels gefactureerd',
  OVERBILLED: 'Overfacturatie',
  PRICE_MISMATCH: 'Prijsafwijking',
  PRICE_LIST_MISMATCH: 'Prijslijstafwijking',
  MISSING_INVOICE: 'Niet gefactureerd',
  UNKNOWN_ARTICLE: 'Onbekend artikel',
  MISMATCH: 'Hoeveelheidsverschil',
  NO_ARTICLE_NUMBER: 'Geen artikelnummer',
  SINGLE_DOCUMENT: 'Enkelvoudig document',
  INCOMPLETE_COMPARISON: 'Factuur ontbreekt — vergelijking beperkt',
  UOM_CONVERSION: 'Eenheidsconversie',
}

export const OVERALL_LABEL: Record<string, string> = {
  OK: 'Akkoord',
  INFO: 'Ter info',
  WARNING: 'Waarschuwing',
  ERROR: 'Fout / afwijking',
  UNKNOWN: 'Onbekend',
}

const DOC_LABEL_NL: Record<string, string> = {
  order_response: 'OrderResponse',
  despatch_advice: 'DespatchAdvice',
  invoice: 'Factuur',
}

function fmt(label: string, value: string | null | undefined): string {
  return `${label.padEnd(20)}: ${value ?? '—'}`
}

function fmtEur(amount: number | null | undefined): string {
  if (amount == null) return '—'
  return `€ ${amount.toFixed(2)}`
}

function buildIncompleteChainNote(session: ReconciliationSession): string[] {
  const missing = [
    session.missing_order_response  ? 'orderbevestiging' : null,
    session.missing_despatch_advice ? 'leveringsbericht'  : null,
    session.missing_invoice         ? 'factuur'           : null,
  ].filter(Boolean) as string[]
  if (missing.length === 0) return []
  return [
    '',
    '=== ONVOLLEDIGE DOCUMENTSET ===',
    `Ontbrekend: ${missing.join(', ')}.`,
    'Vergelijking uitgevoerd op de aanwezige documenten; dit bewijst geen volledige keten.',
  ]
}

function buildDocumentMeldingen(session: ReconciliationSession): string[] {
  const fouten: string[] = []
  const waarschuwingen: string[] = []

  const missingProject = [
    session.ordrsp_missing_project  ? 'OrderResponse'  : null,
    session.desadv_missing_project  ? 'DespatchAdvice' : null,
    session.invoice_missing_project ? 'Factuur'        : null,
  ].filter(Boolean) as string[]
  if (missingProject.length > 0)
    fouten.push(`Projectnummer ontbreekt in: ${missingProject.join(', ')}`)

  const perDoc = [
    { label: 'OrderResponse',  count: session.ordrsp_skipped_lines  ?? 0 },
    { label: 'DespatchAdvice', count: session.desadv_skipped_lines  ?? 0 },
    { label: 'Factuur',        count: session.invoice_skipped_lines ?? 0 },
  ].filter(d => d.count > 0)
  for (const d of perDoc)
    fouten.push(`${d.label}: ${d.count} regel${d.count > 1 ? 's' : ''} zonder artikelnummer`)

  // Document-referentie ontbreekt/wijkt af: WhiteVision processing error (FOUT), niet
  // langer WARN — zie XMLInsights-SDD requirements/processing-profiles.md
  // REQ-PROCESSING-PROFILES-005 (severity-alignment 2026-09-14).
  if (session.ordrsp_desadv_ref_mismatch) {
    fouten.push(
      session.desadv_ordrsp_ref
        ? `OR-nr in DespatchAdvice (${session.desadv_ordrsp_ref}) komt niet overeen met OR-nr (${session.order_response_number ?? '—'})`
        : `OR-nr ontbreekt in DespatchAdvice (verwacht: ${session.order_response_number ?? '—'})`
    )
  }
  if (session.ordrsp_invoice_ref_mismatch) {
    fouten.push(
      session.invoice_ordrsp_ref
        ? `OR-nr in factuur (${session.invoice_ordrsp_ref}) komt niet overeen met OR-nr (${session.order_response_number ?? '—'})`
        : `OR-nr ontbreekt in factuur (verwacht: ${session.order_response_number ?? '—'})`
    )
  }
  if (session.desadv_invoice_ref_mismatch) {
    fouten.push(
      session.invoice_desadv_ref
        ? `DA-nr in factuur (${session.invoice_desadv_ref}) komt niet overeen met DA-nr (${session.desadv_number ?? '—'})`
        : `DA-nr ontbreekt in factuur (verwacht: ${session.desadv_number ?? '—'})`
    )
  }
  if (session.project_number_mismatch) {
    // PROJECT_NUMBER_MISMATCH — toon alleen daadwerkelijk aanwezige projectnummers.
    const parts = [
      session.ordrsp_project_number  ? `OrderResponse=${session.ordrsp_project_number}`   : null,
      session.desadv_project_number  ? `DespatchAdvice=${session.desadv_project_number}`   : null,
      session.invoice_project_number ? `Invoice=${session.invoice_project_number}`         : null,
    ].filter(Boolean)
    waarschuwingen.push(`Projectnummer verschilt tussen documenten: ${parts.join(', ')}`)
  }

  const xsdWarnings = session.xsd_warnings ?? {}
  const xsdEntries = Object.entries(xsdWarnings).filter(([, msgs]) => msgs.length > 0)

  if (fouten.length === 0 && waarschuwingen.length === 0 && xsdEntries.length === 0)
    return []

  const result: string[] = ['', '=== DOCUMENTMELDINGEN ===']
  for (const f of fouten)       result.push(`  [FOUT]   ${f}`)
  for (const w of waarschuwingen) result.push(`  [WARN]   ${w}`)
  if (xsdEntries.length > 0) {
    result.push('  [XSD]    Afwijkingen van het officiële SALES005-schema:')
    for (const [docKey, msgs] of xsdEntries) {
      result.push(`    ${DOC_LABEL_NL[docKey] ?? docKey}:`)
      for (const msg of msgs) result.push(`      - ${msg}`)
    }
  }
  return result
}

export function buildDraftBody(session: ReconciliationSession): string {
  const date = new Date(session.created_at).toLocaleDateString('nl-NL')
  const supplier = session.supplier_name ?? session.ordrsp_supplier_name ?? session.invoice_supplier_name ?? '—'
  const buyer = session.ordrsp_buyer_name ?? session.invoice_buyer_name ?? '—'

  const nonOkLines = session.lines.filter(l =>
    l.status !== 'OK' && l.status !== 'SINGLE_DOCUMENT' && l.status !== 'INCOMPLETE_COMPARISON'
  )
  const okLines = session.lines.filter(l => l.status === 'OK')

  const lines: string[] = [
    'Geachte,',
    '',
    'Hierbij de bevindingen van de documentvergelijking. De oorspronkelijke XML-bestanden zijn bijgevoegd.',
    '',
    '=== SAMENVATTING ===',
    fmt('Algehele status', OVERALL_LABEL[session.overall_status] ?? session.overall_status),
    fmt('Project', session.project_number),
    fmt('Leverancier', supplier),
    fmt('Koper', buyer),
    fmt('Datum vergelijking', date),
    '',
    '=== DOCUMENTREFERENTIES ===',
    fmt('OrderResponse', session.order_response_number),
    fmt('DespatchAdvice', session.desadv_number),
    fmt('Factuur', session.invoice_number),
  ]

  lines.push(...buildIncompleteChainNote(session))
  lines.push(...buildDocumentMeldingen(session))

  if (nonOkLines.length > 0) {
    lines.push('')
    lines.push(`=== AFWIJKINGEN (${nonOkLines.length} regel${nonOkLines.length !== 1 ? 's' : ''}) ===`)
    for (const line of nonOkLines) {
      const desc = line.description ? ` — ${line.description}` : ''
      const details = line.status_details ? ` (${line.status_details})` : ''
      lines.push(`  Artikel ${line.article_number}${desc}`)
      lines.push(`    Status : ${STATUS_LABEL[line.status] ?? line.status}${details}`)
      if (line.ordrsp_qty != null || line.desadv_qty != null || line.invoice_qty != null) {
        const unit = line.unit ? ` ${line.unit}` : ''
        lines.push(`    Aant.  : OR=${line.ordrsp_qty ?? '—'}${unit}  DA=${line.desadv_qty ?? '—'}${unit}  INV=${line.invoice_qty ?? '—'}${unit}`)
      }
      if (line.ordrsp_price != null || line.invoice_price != null) {
        lines.push(`    Prijs  : OR=${fmtEur(line.ordrsp_price)}  INV=${fmtEur(line.invoice_price)}`)
      }
    }
  } else {
    lines.push('')
    lines.push('=== BEVINDINGEN ===')
    lines.push(`Alle ${okLines.length} regel${okLines.length !== 1 ? 's' : ''} akkoord — geen afwijkingen geconstateerd.`)
  }

  if (session.total_invoice_amount != null || session.total_net_amount != null) {
    lines.push('')
    lines.push('=== FACTUURTOTALEN ===')
    if (session.total_net_amount != null) lines.push(fmt('Nettobedrag', fmtEur(session.total_net_amount)))
    if (session.total_vat_amount != null) lines.push(fmt('BTW', fmtEur(session.total_vat_amount)))
    if (session.total_charge_allowance != null && session.total_charge_allowance !== 0)
      lines.push(fmt('Toeslag/korting', fmtEur(session.total_charge_allowance)))
    if (session.total_invoice_amount != null) lines.push(fmt('Factuurbedrag', fmtEur(session.total_invoice_amount)))
  }

  lines.push('')
  lines.push('Met vriendelijke groet,')
  return lines.join('\n')
}

export function buildDraftSubject(session: ReconciliationSession): string {
  const supplier = session.supplier_name ?? session.ordrsp_supplier_name ?? ''
  const or = session.order_response_number ? `OR ${session.order_response_number}` : ''
  const inv = session.invoice_number ? `INV ${session.invoice_number}` : ''
  const ref = [or, inv].filter(Boolean).join(' / ') || `sessie #${session.id}`
  const status = OVERALL_LABEL[session.overall_status] ?? session.overall_status
  return `Bevindingen vergelijking ${ref}${supplier ? ` — ${supplier}` : ''} [${status}]`
}

function safeN(v: string | null | undefined): string {
  const s = (v ?? '').replace(/[^\w\-. ]/g, '').trim()
  return s || 'UNKNOWN'
}

function safeCo(v: string | null | undefined): string {
  return (v ?? '')
    .replace(/\b(B\.?V\.?|N\.?V\.?|B\.?V|N\.?V|VOF|B\.V\.|N\.V\.)\b/gi, '')
    .replace(/[^\w\- ]/g, '')
    .replace(/\s+/g, '')
    .trim()
}

function partiesName(
  sGln: string | null | undefined,
  sName: string | null | undefined,
  bGln: string | null | undefined,
  bName: string | null | undefined,
  refs: string[],
  docType: string,
): string {
  const sg = safeN(sGln)
  const sn = safeCo(sName)
  const bg = safeN(bGln)
  const bn = safeCo(bName)
  return [sg + (sn ? `_${sn}` : ''), bg + (bn ? `_${bn}` : ''), ...refs, docType].filter(Boolean).join('_') + '.xml'
}

export function computePartiesFilenames(session: ReconciliationSession): Record<string, string> {
  return {
    order_response: partiesName(
      session.ordrsp_supplier_gln, session.ordrsp_supplier_name,
      session.ordrsp_buyer_gln, session.ordrsp_buyer_name,
      [`OR${safeN(session.order_response_number)}`],
      'OrderResponse',
    ),
    despatch_advice: partiesName(
      session.desadv_supplier_gln, session.desadv_supplier_name,
      session.desadv_buyer_gln, session.desadv_buyer_name,
      [`OR${safeN(session.desadv_ordrsp_ref)}`, `DA${safeN(session.desadv_number)}`],
      'DespatchAdvice',
    ),
    invoice: partiesName(
      session.invoice_supplier_gln, session.invoice_supplier_name,
      session.invoice_buyer_gln, session.invoice_buyer_name,
      [`OR${safeN(session.invoice_ordrsp_ref)}`, `DA${safeN(session.invoice_desadv_ref)}`, `INV${safeN(session.invoice_number)}`],
      'Invoice',
    ),
  }
}
